/**
 * Client-side session expiry: auto logout after 24 hours.
 * Extend session when token is refreshed (e.g. getUserDetails).
 */
import buildInfo from './buildVersion.json';

const SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const EXPIRES_AT_KEY = 'authSessionExpiresAt';

/**
 * Regenerated automatically on every `npm run build` (scripts/generate-build-version.js).
 * Sessions stamped with an older (or missing) version are force-logged-out on
 * their next page load or API request, so a deploy closes all existing sessions.
 */
export const APP_VERSION = buildInfo.version;
const APP_VERSION_KEY = 'appVersion';

export function setAuthSessionExpiry() {
  const expiresAt = Date.now() + SESSION_DURATION_MS;
  localStorage.setItem(EXPIRES_AT_KEY, String(expiresAt));
  localStorage.setItem(APP_VERSION_KEY, APP_VERSION);
}

/**
 * Wipe all client-side cached data (token, expiry, version stamp, and any
 * legacy/leftover keys) so the next login starts from a clean slate.
 */
export function clearAuthSession() {
  localStorage.clear();
  sessionStorage.clear();
}

/**
 * True if the session is no longer valid:
 * - the 24h expiry has passed, or
 * - the app was redeployed since this session was stamped.
 * If token exists but no expiry (legacy), start 24h window from now once.
 */
export function isSessionExpired() {
  const token = localStorage.getItem('authToken');
  if (!token) return false;
  if (localStorage.getItem(APP_VERSION_KEY) !== APP_VERSION) return true;
  let expiresAt = localStorage.getItem(EXPIRES_AT_KEY);
  if (!expiresAt) {
    setAuthSessionExpiry();
    return false;
  }
  return Date.now() > Number(expiresAt);
}

/**
 * If session expired, clear storage and return true (caller should redirect).
 */
export function logoutIfSessionExpired() {
  if (!isSessionExpired()) return false;
  clearAuthSession();
  return true;
}
